import ApiCollection       from '@widesk-core/models/ApiCollection';
import ProjectVersionModel from '@models/map/ProjectVersionModel';
import ProjectModel        from '@models/map/ProjectModel';
import { action }          from 'mobx';
import { computed }        from 'mobx';
import { makeObservable }  from 'mobx';
import { observable }      from 'mobx';
import { runInAction }     from 'mobx';

/** Version de schéma appliquée à TOUTES les cartes d'une publication. */
export const JSON_SCHEMA_VERSION = 4;

/** Rythme et durée maximale du suivi d'une publication en cours. */
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * La dernière publication du projet, et son achèvement.
 *
 * `POST /project_versions` rend la main AVANT que la publication soit faite : un consumer crée
 * ensuite la version de chaque carte, la compile, puis assemble le fichier condensé du projet. Le
 * seul témoin de fin est `ProjectVersion.fileUrl`, d'où le suivi par interrogation périodique tenu
 * ici — sans lui, l'interface annoncerait "publié" alors que la route publique du projet répond
 * encore avec des cartes vides.
 *
 * Le suivi reprend aussi à l'OUVERTURE du projet (`load`) : une publication lancée juste avant un
 * F5, ou depuis un autre onglet, est toujours en cours et doit s'afficher comme telle.
 */
class ProjectPublicationStore {
	/** La publication la plus récente du projet, achevée ou non. */
	@observable.ref lastVersion?: ProjectVersionModel;

	@observable isLoaded = false;

	private _timeout?: ReturnType<typeof setTimeout>;

	private _pollingUntil = 0;

	constructor() {
		makeObservable(this);
	}

	/** Publication en cours : demandée, mais dont le fichier condensé n'existe pas encore. */
	@computed get isPublishing() {
		return !!this.lastVersion && !this.lastVersion.isPublished;
	}

	/** Le projet a déjà une adresse publique servie : au moins une publication est allée au bout. */
	@computed get isPublishedOnce() {
		return !!this.lastVersion?.isPublished;
	}

	public async load(projectId: id) {
		const collection = new ApiCollection(ProjectVersionModel);

		await collection
			.setRequiredFilter('project', projectId)
			.setSort('number', 'desc')
			.list({ params: { itemsPerPage: 1 } }); // Seule la dernière nous intéresse

		runInAction(() => {
			this.lastVersion = collection.first() || undefined;
			this.isLoaded = true;
		});

		if (this.isPublishing) this._startPolling();

		return this.lastVersion;
	}

	/**
	 * Demande la publication du projet. `maps` est volontairement omis : absent, il publie TOUTES les
	 * cartes du projet, et c'est la seule forme de publication que le backoffice propose.
	 */
	public async publish(options: { project: ProjectModel; comment?: string }) {
		const version = new ProjectVersionModel();

		await version.patch({
			project: options.project.urn,
			jsonSchemaVersion: JSON_SCHEMA_VERSION,
			comment: options.comment || undefined,
		});

		this._setLastVersion(version);
		this._startPolling();

		return version;
	}

	public clear() {
		this._stopPolling();

		runInAction(() => {
			this.lastVersion = undefined;
			this.isLoaded = false;
		});
	}

	@action private _setLastVersion(version: ProjectVersionModel) {
		this.lastVersion = version;
	}

	/**
	 * Le fichier condensé se fait attendre plus ou moins longtemps selon le nombre de cartes : plutôt
	 * qu'un nombre d'essais, une échéance. Passée celle-ci on cesse d'interroger — l'interface reste
	 * sur "publication en cours", qu'un rechargement tranchera.
	 */
	private _startPolling() {
		this._stopPolling();
		this._pollingUntil = Date.now() + POLL_TIMEOUT_MS;
		this._schedulePoll();
	}

	private _schedulePoll() {
		this._timeout = setTimeout(() => void this._poll(), POLL_INTERVAL_MS);
	}

	private async _poll() {
		const version = this.lastVersion;

		if (!version?.id) return;

		try {
			await version.fetch();
		} catch (err) {
			// Sans conséquence : la publication suit son cours côté API, on retentera au prochain tour.
			console.error('Suivi de la publication du projet', err);
		}

		// Projet refermé, ou publication remplacée par une plus récente pendant l'appel : ce tour-ci
		// ne pilote plus rien.
		if (this.lastVersion !== version) return;

		if (version.isPublished) {
			this._stopPolling();
			return;
		}

		if (Date.now() >= this._pollingUntil) {
			this._stopPolling();
			return;
		}

		this._schedulePoll();
	}

	private _stopPolling() {
		clearTimeout(this._timeout);
		this._timeout = undefined;
	}
}

export default new ProjectPublicationStore();
